/**
 * Renders one list counter value in one of WordprocessingML's number formats
 * (`w:numFmt`, ST_NumberFormat), the way Word paints the number in front of a
 * list paragraph.
 *
 * Every value of the enumeration is rendered by one of nine categories, and a
 * category is one algorithm over a lookup table: the same code renders
 * `decimal` and Thai digits, the same code renders `lowerLetter` and Hiragana,
 * the same code renders Japanese and Korean counting words. No format is a
 * special case; adding one is adding a table row.
 *
 * CATEGORIES
 *   - digits        each decimal digit of the value replaced by the script's digit
 *   - enclosed      a table of pre-composed glyphs for the first values, decimal beyond it
 *   - alphabetic    the value spelled with a repeated letter of an alphabet: 1 -> a, 27 -> aa
 *   - roman         Roman numerals, upper or lower case
 *   - additive      symbols whose values add up, as Hebrew letter numerals do
 *   - counting      a positional system with unit words, as Chinese, Japanese and Korean numerals
 *   - words         the number spelled out in a language: English, Vietnamese, Thai, Hindi
 *   - symbols       a symbol sequence that repeats with a growing count, as the Chicago style does
 *   - literal       the level text glyph itself: bullets, and `none`
 *
 * A format the enumeration does not name, or `custom`, renders as decimal so
 * the label is never silently missing.
 */

/** How a category turns a counter value into text; the level text literal is only meaningful for `literal`. */
type ListNumberRenderer = (value: number, levelTextLiteral: string) => string;

/**
 * Replaces each decimal digit by the digit at the same position of `digitGlyphs`
 * (ten glyphs, 0 to 9).
 *
 * EXAMPLE
 *   digitGlyphs = "๐๑๒๓๔๕๖๗๘๙", value = 12 -> "๑๒"
 */
function digitsRenderer(digitGlyphs: string, minimumWidth: number): ListNumberRenderer {
    const glyphs = Array.from(digitGlyphs);
    return (value) => String(Math.abs(value)).padStart(minimumWidth, "0").split("").map((digit) => glyphs[Number(digit)]).join("");
}

/**
 * Pre-composed glyphs for the values 1 to `glyphs.length`; a value past the
 * table renders as decimal, which is what Word shows for the 21st circled number.
 *
 * EXAMPLE
 *   glyphs = "①②③…⑳", value = 3  -> "③"
 *   glyphs = "①②③…⑳", value = 21 -> "21"
 */
function enclosedRenderer(glyphs: string): ListNumberRenderer {
    const table = Array.from(glyphs);
    return (value) => (value >= 1 && value <= table.length ? table[value - 1] : String(value));
}

/**
 * The value spelled with one letter of the alphabet, repeated once more for
 * every full pass through it, as Word does for `lowerLetter`.
 *
 * EXAMPLE
 *   alphabet = "a…z" (26 letters)
 *   value = 1  -> index (1 - 1) % 26 = 0, repeats floor(0 / 26) + 1 = 1 -> "a"
 *   value = 27 -> index 26 % 26 = 0,      repeats floor(26 / 26) + 1 = 2 -> "aa"
 *   value = 52 -> index 51 % 26 = 25,     repeats floor(51 / 26) + 1 = 2 -> "zz"
 *
 * EDGE CASE
 *   value 0 or negative has no letter; Word paints "0" there, so decimal is returned.
 */
function alphabeticRenderer(alphabet: string | readonly string[]): ListNumberRenderer {
    const letters = typeof alphabet === "string" ? Array.from(alphabet) : alphabet;
    return (value) => {
        if (value < 1) return String(value);
        const letterIndex = (value - 1) % letters.length;
        const repeats = Math.floor((value - 1) / letters.length) + 1;
        return letters[letterIndex].repeat(repeats);
    };
}

const ROMAN_NUMERAL_TABLE: readonly (readonly [number, string])[] = [
    [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"], [100, "C"], [90, "XC"], [50, "L"], [40, "XL"], [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
];

/**
 * Subtractive Roman numerals, as Word writes them.
 *
 * EXAMPLE
 *   value = 1994 -> M (994) CM (94) XC (4) IV -> "MCMXCIV"
 *
 * EDGE CASE
 *   Roman numerals have no zero; Word paints "0", so decimal is returned for values below 1.
 */
function romanRenderer(lowerCase: boolean): ListNumberRenderer {
    return (value) => {
        if (value < 1) return String(value);
        let remaining = value;
        let numeral = "";
        for (const [weight, symbol] of ROMAN_NUMERAL_TABLE) {
            while (remaining >= weight) {
                numeral += symbol;
                remaining -= weight;
            }
        }
        return lowerCase ? numeral.toLowerCase() : numeral;
    };
}

/**
 * Additive numerals: the largest symbol not above the remainder is taken
 * repeatedly, so the symbols' values sum to the value. Hebrew letter numerals
 * work this way, with two traditional exceptions: a remainder of exactly 15 or
 * 16 is written with its own pair (טו, טז) instead of 10 + 5 and 10 + 6, which
 * would spell a divine name.
 *
 * EXAMPLE
 *   value = 18  -> י (10) then ח (8)        -> "יח"
 *   value = 15  -> exception               -> "טו"
 *   value = 115 -> ק (100) then exception  -> "קטו"
 */
function additiveRenderer(symbolValues: readonly (readonly [number, string])[], exceptions: ReadonlyMap<number, string>): ListNumberRenderer {
    return (value) => {
        if (value < 1) return String(value);
        let remaining = value;
        let numeral = "";
        for (const [weight, symbol] of symbolValues) {
            /**
             * WHEN what is left is one of the exceptional values
             *   CONSEQUENCE
             *     its own spelling closes the numeral; nothing smaller is added.
             */
            const exception = exceptions.get(remaining);
            if (exception !== undefined) return numeral + exception;
            while (remaining >= weight) {
                numeral += symbol;
                remaining -= weight;
            }
        }
        return numeral;
    };
}

/** A counting system: the ten digit words and the unit words for ten, hundred, thousand and ten-thousand. */
interface CountingSystem {
    digits: string;
    ten: string;
    hundred: string;
    thousand: string;
    tenThousand: string;
    /** WHEN true a leading one before a unit is written (壹拾 in legal styles); WHEN false ten to nineteen start with the unit alone (十一). */
    writesLeadingOne: boolean;
}

/**
 * Positional counting with unit words, as Chinese, Japanese and Korean list
 * numbers are written: each digit is followed by its unit, zero digits are
 * dropped, and the first ten-thousand group is written before its unit.
 *
 * EXAMPLE
 *   Japanese counting, value = 21  -> 二十一
 *   Japanese counting, value = 105 -> 百五
 *   Japanese counting, value = 12  -> 十二   (no leading one before 十 in the common style)
 *   Japanese legal,    value = 12  -> 壹拾貳 (the legal style writes the leading one)
 */
function countingRenderer(system: CountingSystem): ListNumberRenderer {
    const digits = Array.from(system.digits);
    const renderBelowTenThousand = (value: number): string => {
        const units = [system.thousand, system.hundred, system.ten, ""];
        const weights = [1000, 100, 10, 1];
        let text = "";
        let remaining = value;
        weights.forEach((weight, position) => {
            const digit = Math.floor(remaining / weight);
            remaining -= digit * weight;
            /**
             * WHEN this position holds a zero
             *   CONSEQUENCE
             *     nothing is written for it; these systems do not write internal zeros in list numbers.
             */
            if (digit === 0) return;
            /**
             * CASES
             *   - a one before a unit in the common style -> the unit alone (十, 百)
             *   - any other digit, or the legal style     -> the digit word then the unit
             */
            const writesDigit = digit !== 1 || units[position] === "" || system.writesLeadingOne;
            text += (writesDigit ? digits[digit] : "") + units[position];
        });
        return text;
    };
    return (value) => {
        if (value < 1) return digits[0];
        const tenThousands = Math.floor(value / 10000);
        const remainder = value % 10000;
        const highGroup = tenThousands === 0 ? "" : renderBelowTenThousand(tenThousands) + system.tenThousand;
        return highGroup + (remainder === 0 ? "" : renderBelowTenThousand(remainder));
    };
}

/**
 * How a language spells numbers: the words for 0 to 19, the tens, and how the
 * hundreds and thousands compose. `belowTwenty[0]` is the word for zero.
 */
interface NumberWords {
    belowTwenty: readonly string[];
    tens: readonly string[];
    hundred: string;
    thousand: string;
    /** The separator between a tens word and the following unit word ("-" in English, " " elsewhere). */
    tensUnitJoiner: string;
    /** WHEN true a lone hundred is written with a leading one ("một trăm"); English writes "One Hundred" too. */
    hundredsWriteOne: boolean;
    /** Irregular spellings by value, checked before composition (Vietnamese "lăm", Thai "เอ็ด", Hindi's irregular teens and tens). */
    irregular: ReadonlyMap<number, string>;
}

/**
 * Spells a value below one million in a language from its tables. English
 * capitalises each word as Word does for `cardinalText` ("Twenty-One").
 *
 * EXAMPLE
 *   English, value = 21   -> "Twenty-One"
 *   English, value = 105  -> "One Hundred Five"
 *   English, value = 1200 -> "One Thousand Two Hundred"
 */
function wordsRenderer(words: NumberWords): ListNumberRenderer {
    const spellBelowHundred = (value: number): string => {
        const irregular = words.irregular.get(value);
        if (irregular !== undefined) return irregular;
        if (value < 20) return words.belowTwenty[value];
        const tensWord = words.tens[Math.floor(value / 10)];
        const unit = value % 10;
        if (unit === 0) return tensWord;
        return tensWord + words.tensUnitJoiner + (words.irregular.get(-unit) ?? words.belowTwenty[unit]);
    };
    const spellBelowThousand = (value: number): string => {
        const irregular = words.irregular.get(value);
        if (irregular !== undefined) return irregular;
        if (value < 100) return spellBelowHundred(value);
        const hundreds = Math.floor(value / 100);
        const rest = value % 100;
        const hundredsText = (hundreds === 1 && !words.hundredsWriteOne ? "" : words.belowTwenty[hundreds] + " ") + words.hundred;
        return rest === 0 ? hundredsText : `${hundredsText} ${spellBelowHundred(rest)}`;
    };
    return (value) => {
        if (value < 0) return String(value);
        if (value < 1000) return spellBelowThousand(value);
        const thousands = Math.floor(value / 1000);
        const rest = value % 1000;
        const thousandsText = `${spellBelowThousand(thousands)} ${words.thousand}`;
        return rest === 0 ? thousandsText : `${thousandsText} ${spellBelowThousand(rest)}`;
    };
}

const ENGLISH_WORDS: NumberWords = {
    belowTwenty: ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"],
    tens: ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"],
    hundred: "Hundred",
    thousand: "Thousand",
    tensUnitJoiner: "-",
    hundredsWriteOne: true,
    irregular: new Map(),
};

/**
 * English ordinal words follow the cardinal words with the last word changed:
 * "Twenty-One" -> "Twenty-First", "Twelve" -> "Twelfth", "Ninety" -> "Ninetieth".
 */
const ENGLISH_ORDINAL_LAST_WORD: ReadonlyMap<string, string> = new Map([
    ["One", "First"], ["Two", "Second"], ["Three", "Third"], ["Five", "Fifth"], ["Eight", "Eighth"], ["Nine", "Ninth"], ["Twelve", "Twelfth"],
    ["Twenty", "Twentieth"], ["Thirty", "Thirtieth"], ["Forty", "Fortieth"], ["Fifty", "Fiftieth"], ["Sixty", "Sixtieth"], ["Seventy", "Seventieth"], ["Eighty", "Eightieth"], ["Ninety", "Ninetieth"],
]);

/**
 * `ordinalText`: the cardinal words with the final word made ordinal.
 *
 * EXAMPLE
 *   value = 21 -> cardinal "Twenty-One" -> "Twenty-First"
 *   value = 100 -> "One Hundred" -> "One Hundredth"
 */
function englishOrdinalWordsRenderer(): ListNumberRenderer {
    const cardinal = wordsRenderer(ENGLISH_WORDS);
    return (value) => {
        const words = cardinal(value, "");
        const lastWordStart = Math.max(words.lastIndexOf(" "), words.lastIndexOf("-")) + 1;
        const lastWord = words.slice(lastWordStart);
        const ordinalLastWord = ENGLISH_ORDINAL_LAST_WORD.get(lastWord) ?? `${lastWord}th`;
        return words.slice(0, lastWordStart) + ordinalLastWord;
    };
}

/**
 * `ordinal`: the decimal value with the English suffix Word uses.
 *
 * CASES
 *   - 11, 12, 13 and every value ending in them -> "th" (11th, 112th)
 *   - ends in 1                                  -> "st"
 *   - ends in 2                                  -> "nd"
 *   - ends in 3                                  -> "rd"
 *   - anything else                              -> "th"
 */
function englishOrdinalSuffixRenderer(): ListNumberRenderer {
    return (value) => {
        const lastTwo = Math.abs(value) % 100;
        const last = Math.abs(value) % 10;
        const suffix = lastTwo >= 11 && lastTwo <= 13 ? "th" : last === 1 ? "st" : last === 2 ? "nd" : last === 3 ? "rd" : "th";
        return `${value}${suffix}`;
    };
}

/**
 * `dollarText`: the amount in words with the cents fraction, as Word writes a
 * cheque line ("One and 00/100"). A list counter has no cents, so the fraction
 * is always zero.
 */
function englishDollarTextRenderer(): ListNumberRenderer {
    const cardinal = wordsRenderer(ENGLISH_WORDS);
    return (value) => `${cardinal(value, "")} and 00/100`;
}

const VIETNAMESE_WORDS: NumberWords = {
    belowTwenty: ["không", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín", "mười", "mười một", "mười hai", "mười ba", "mười bốn", "mười lăm", "mười sáu", "mười bảy", "mười tám", "mười chín"],
    tens: ["", "mười", "hai mươi", "ba mươi", "bốn mươi", "năm mươi", "sáu mươi", "bảy mươi", "tám mươi", "chín mươi"],
    hundred: "trăm",
    thousand: "nghìn",
    tensUnitJoiner: " ",
    hundredsWriteOne: true,
    /** After a tens word Vietnamese says "mốt" for one and "lăm" for five; negative keys are unit words after a tens word. */
    irregular: new Map([[-1, "mốt"], [-5, "lăm"]]),
};

const THAI_WORDS: NumberWords = {
    belowTwenty: ["ศูนย์", "หนึ่ง", "สอง", "สาม", "สี่", "ห้า", "หก", "เจ็ด", "แปด", "เก้า", "สิบ", "สิบเอ็ด", "สิบสอง", "สิบสาม", "สิบสี่", "สิบห้า", "สิบหก", "สิบเจ็ด", "สิบแปด", "สิบเก้า"],
    tens: ["", "สิบ", "ยี่สิบ", "สามสิบ", "สี่สิบ", "ห้าสิบ", "หกสิบ", "เจ็ดสิบ", "แปดสิบ", "เก้าสิบ"],
    hundred: "ร้อย",
    thousand: "พัน",
    tensUnitJoiner: "",
    hundredsWriteOne: true,
    /** After a tens word Thai says "เอ็ด" for one. */
    irregular: new Map([[-1, "เอ็ด"]]),
};

/** Hindi spells every number below one hundred with its own word; the table is the rule. */
const HINDI_BELOW_HUNDRED = [
    "शून्य", "एक", "दो", "तीन", "चार", "पाँच", "छह", "सात", "आठ", "नौ", "दस", "ग्यारह", "बारह", "तेरह", "चौदह", "पंद्रह", "सोलह", "सत्रह", "अठारह", "उन्नीस",
    "बीस", "इक्कीस", "बाईस", "तेईस", "चौबीस", "पच्चीस", "छब्बीस", "सत्ताईस", "अट्ठाईस", "उनतीस",
    "तीस", "इकतीस", "बत्तीस", "तैंतीस", "चौंतीस", "पैंतीस", "छत्तीस", "सैंतीस", "अड़तीस", "उनतालीस",
    "चालीस", "इकतालीस", "बयालीस", "तैंतालीस", "चवालीस", "पैंतालीस", "छियालीस", "सैंतालीस", "अड़तालीस", "उनचास",
    "पचास", "इक्यावन", "बावन", "तिरपन", "चौवन", "पचपन", "छप्पन", "सत्तावन", "अट्ठावन", "उनसठ",
    "साठ", "इकसठ", "बासठ", "तिरसठ", "चौंसठ", "पैंसठ", "छियासठ", "सड़सठ", "अड़सठ", "उनहत्तर",
    "सत्तर", "इकहत्तर", "बहत्तर", "तिहत्तर", "चौहत्तर", "पचहत्तर", "छिहत्तर", "सतहत्तर", "अठहत्तर", "उनासी",
    "अस्सी", "इक्यासी", "बयासी", "तिरासी", "चौरासी", "पचासी", "छियासी", "सत्तासी", "अट्ठासी", "नवासी",
    "नब्बे", "इक्यानवे", "बानवे", "तिरानवे", "चौरानवे", "पंचानवे", "छियानवे", "सत्तानवे", "अट्ठानवे", "निन्यानवे",
];

const HINDI_WORDS: NumberWords = {
    belowTwenty: HINDI_BELOW_HUNDRED.slice(0, 20),
    tens: ["", "दस", "बीस", "तीस", "चालीस", "पचास", "साठ", "सत्तर", "अस्सी", "नब्बे"],
    hundred: "सौ",
    thousand: "हज़ार",
    tensUnitJoiner: " ",
    hundredsWriteOne: true,
    irregular: new Map(HINDI_BELOW_HUNDRED.map((word, value) => [value, word] as const)),
};

/**
 * `bahtText`: the Thai amount in words followed by the currency word for a
 * whole amount ("ห้าบาทถ้วน").
 */
function thaiBahtTextRenderer(): ListNumberRenderer {
    const thai = wordsRenderer(THAI_WORDS);
    return (value) => `${thai(value, "")}บาทถ้วน`;
}

/**
 * A symbol sequence that starts over with one more repetition each pass, as
 * the Chicago Manual footnote marks do: *, †, ‡, §, **, ††, ‡‡, §§, ***…
 *
 * EXAMPLE
 *   symbols = "*†‡§", value = 5 -> index (5 - 1) % 4 = 0, repeats floor(4 / 4) + 1 = 2 -> "**"
 */
function symbolsRenderer(symbols: string): ListNumberRenderer {
    return alphabeticRenderer(symbols);
}

/** `bullet` and `none`: the level text is the whole label, a glyph or nothing. */
function literalRenderer(): ListNumberRenderer {
    return (_value, levelTextLiteral) => levelTextLiteral;
}

const DECIMAL_DIGITS = "0123456789";
const HEBREW_LETTER_NUMERALS: readonly (readonly [number, string])[] = [
    [400, "ת"], [300, "ש"], [200, "ר"], [100, "ק"], [90, "צ"], [80, "פ"], [70, "ע"], [60, "ס"], [50, "נ"], [40, "מ"], [30, "ל"], [20, "כ"],
    [10, "י"], [9, "ט"], [8, "ח"], [7, "ז"], [6, "ו"], [5, "ה"], [4, "ד"], [3, "ג"], [2, "ב"], [1, "א"],
];
const HEBREW_LETTER_NUMERAL_EXCEPTIONS: ReadonlyMap<number, string> = new Map([[15, "טו"], [16, "טז"]]);

const CJK_COMMON_DIGITS = "〇一二三四五六七八九";
/**
 * The sixty stem-branch pairs of the traditional East Asian cycle, each two
 * characters, so the alphabetic renderer takes them as a list rather than a
 * string of single characters.
 */
const SEXAGENARY_CYCLE: readonly string[] = (
    "甲子乙丑丙寅丁卯戊辰己巳庚午辛未壬申癸酉甲戌乙亥丙子丁丑戊寅己卯庚辰辛巳壬午癸未甲申乙酉丙戌丁亥戊子己丑庚寅辛卯壬辰癸巳甲午乙未丙申丁酉戊戌己亥庚子辛丑壬寅癸卯甲辰乙巳丙午丁未戊申己酉庚戌辛亥壬子癸丑甲寅乙卯丙辰丁巳戊午己未庚申辛酉壬戌癸亥"
).match(/.{2}/g)!;
const CJK_LEGAL_TRADITIONAL_DIGITS = "零壹貳參肆伍陸柒捌玖";
const CJK_LEGAL_SIMPLIFIED_DIGITS = "零壹贰叁肆伍陆柒捌玖";
const KOREAN_SINO_DIGITS = "영일이삼사오육칠팔구";

/**
 * Every ST_NumberFormat value, mapped to the category and table that renders
 * it. `decimal` renders values the same way whichever category a document
 * expects for an unlisted format, so the table's fallback is decimal.
 */
const RENDERERS_BY_NUMBER_FORMAT: ReadonlyMap<string, ListNumberRenderer> = new Map<string, ListNumberRenderer>([
    ["decimal", digitsRenderer(DECIMAL_DIGITS, 1)],
    ["decimalZero", digitsRenderer(DECIMAL_DIGITS, 2)],
    ["decimalFullWidth", digitsRenderer("０１２３４５６７８９", 1)],
    ["decimalFullWidth2", digitsRenderer("０１２３４５６７８９", 1)],
    ["decimalHalfWidth", digitsRenderer(DECIMAL_DIGITS, 1)],
    ["arabicAlpha", alphabeticRenderer("أبتثجحخدذرزسشصضطظعغفقكلمنهوي")],
    ["arabicAbjad", alphabeticRenderer("أبجدهوزحطيكلمنسعفصقرشتثخذضظغ")],
    ["hindiNumbers", digitsRenderer("०१२३४५६७८९", 1)],
    ["hindiVowels", alphabeticRenderer("अआइईउऊएऐओऔ")],
    ["hindiConsonants", alphabeticRenderer("कखगघङचछजझञटठडढणतथदधनपफबभमयरलवशषसह")],
    ["hindiCounting", wordsRenderer(HINDI_WORDS)],
    ["thaiNumbers", digitsRenderer("๐๑๒๓๔๕๖๗๘๙", 1)],
    ["thaiLetters", alphabeticRenderer("กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรลวศษสหฬอฮ")],
    ["thaiCounting", wordsRenderer(THAI_WORDS)],
    ["bahtText", thaiBahtTextRenderer()],
    ["vietnameseCounting", wordsRenderer(VIETNAMESE_WORDS)],
    ["russianLower", alphabeticRenderer("абвгдеёжзийклмнопрстуфхцчшщъыьэюя")],
    ["russianUpper", alphabeticRenderer("АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ")],
    ["hebrew1", alphabeticRenderer("אבגדהוזחטיכלמנסעפצקרשת")],
    ["hebrew2", additiveRenderer(HEBREW_LETTER_NUMERALS, HEBREW_LETTER_NUMERAL_EXCEPTIONS)],
    ["lowerLetter", alphabeticRenderer("abcdefghijklmnopqrstuvwxyz")],
    ["upperLetter", alphabeticRenderer("ABCDEFGHIJKLMNOPQRSTUVWXYZ")],
    ["lowerRoman", romanRenderer(true)],
    ["upperRoman", romanRenderer(false)],
    ["ordinal", englishOrdinalSuffixRenderer()],
    ["cardinalText", wordsRenderer(ENGLISH_WORDS)],
    ["ordinalText", englishOrdinalWordsRenderer()],
    ["dollarText", englishDollarTextRenderer()],
    ["hex", (value) => Math.abs(value).toString(16).toUpperCase()],
    ["chicago", symbolsRenderer("*†‡§")],
    ["numberInDash", (value) => `- ${value} -`],
    ["decimalEnclosedCircle", enclosedRenderer("①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳")],
    ["decimalEnclosedCircleChinese", enclosedRenderer("㊀㊁㊂㊃㊄㊅㊆㊇㊈㊉")],
    ["decimalEnclosedFullstop", enclosedRenderer("⒈⒉⒊⒋⒌⒍⒎⒏⒐⒑⒒⒓⒔⒕⒖⒗⒘⒙⒚⒛")],
    ["decimalEnclosedParen", enclosedRenderer("⑴⑵⑶⑷⑸⑹⑺⑻⑼⑽⑾⑿⒀⒁⒂⒃⒄⒅⒆⒇")],
    ["ideographEnclosedCircle", enclosedRenderer("㊀㊁㊂㊃㊄㊅㊆㊇㊈㊉")],
    ["ideographDigital", digitsRenderer(CJK_COMMON_DIGITS, 1)],
    ["taiwaneseDigital", digitsRenderer(CJK_COMMON_DIGITS, 1)],
    ["koreanDigital", digitsRenderer(KOREAN_SINO_DIGITS, 1)],
    ["koreanDigital2", digitsRenderer(CJK_COMMON_DIGITS, 1)],
    ["ideographTraditional", alphabeticRenderer("甲乙丙丁戊己庚辛壬癸")],
    ["ideographZodiac", alphabeticRenderer("子丑寅卯辰巳午未申酉戌亥")],
    ["ideographZodiacTraditional", alphabeticRenderer(SEXAGENARY_CYCLE)],
    ["aiueo", alphabeticRenderer("ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜｦﾝ")],
    ["aiueoFullWidth", alphabeticRenderer("アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヰヱヲン")],
    ["iroha", alphabeticRenderer("ｲﾛﾊﾆﾎﾍﾄﾁﾘﾇﾙｦﾜｶﾖﾀﾚｿﾂﾈﾅﾗﾑｳヰﾉｵｸﾔﾏｹﾌｺｴﾃｱｻｷﾕﾒﾐｼヱﾋﾓｾｽ")],
    ["irohaFullWidth", alphabeticRenderer("イロハニホヘトチリヌルヲワカヨタレソツネナラムウヰノオクヤマケフコエテアサキユメミシヱヒモセス")],
    ["japaneseCounting", countingRenderer({ digits: CJK_COMMON_DIGITS, ten: "十", hundred: "百", thousand: "千", tenThousand: "万", writesLeadingOne: false })],
    ["japaneseDigitalTenThousand", countingRenderer({ digits: CJK_COMMON_DIGITS, ten: "十", hundred: "百", thousand: "千", tenThousand: "万", writesLeadingOne: false })],
    ["japaneseLegal", countingRenderer({ digits: CJK_LEGAL_TRADITIONAL_DIGITS, ten: "拾", hundred: "佰", thousand: "仟", tenThousand: "萬", writesLeadingOne: true })],
    ["chineseCounting", countingRenderer({ digits: CJK_COMMON_DIGITS, ten: "十", hundred: "百", thousand: "千", tenThousand: "万", writesLeadingOne: false })],
    ["chineseCountingThousand", countingRenderer({ digits: CJK_COMMON_DIGITS, ten: "十", hundred: "百", thousand: "千", tenThousand: "万", writesLeadingOne: false })],
    ["chineseLegalSimplified", countingRenderer({ digits: CJK_LEGAL_SIMPLIFIED_DIGITS, ten: "拾", hundred: "佰", thousand: "仟", tenThousand: "万", writesLeadingOne: true })],
    ["taiwaneseCounting", countingRenderer({ digits: CJK_COMMON_DIGITS, ten: "十", hundred: "百", thousand: "千", tenThousand: "萬", writesLeadingOne: false })],
    ["taiwaneseCountingThousand", countingRenderer({ digits: CJK_COMMON_DIGITS, ten: "十", hundred: "百", thousand: "千", tenThousand: "萬", writesLeadingOne: false })],
    ["ideographLegalTraditional", countingRenderer({ digits: CJK_LEGAL_TRADITIONAL_DIGITS, ten: "拾", hundred: "佰", thousand: "仟", tenThousand: "萬", writesLeadingOne: true })],
    ["koreanCounting", countingRenderer({ digits: KOREAN_SINO_DIGITS, ten: "십", hundred: "백", thousand: "천", tenThousand: "만", writesLeadingOne: false })],
    ["koreanLegal", countingRenderer({ digits: "영일이삼사오육칠팔구", ten: "십", hundred: "백", thousand: "천", tenThousand: "만", writesLeadingOne: true })],
    ["ganada", alphabeticRenderer("가나다라마바사아자차카타파하")],
    ["chosung", alphabeticRenderer("ㄱㄴㄷㄹㅁㅂㅅㅇㅈㅊㅋㅌㅍㅎ")],
    ["custom", digitsRenderer(DECIMAL_DIGITS, 1)],
    ["bullet", literalRenderer()],
    ["none", literalRenderer()],
]);

/** Every number format the table renders, so a test can hold it against the schema's enumeration. */
export const LIST_NUMBER_FORMATS_HANDLED: readonly string[] = Array.from(RENDERERS_BY_NUMBER_FORMAT.keys());

/**
 * The counter value rendered in `numberFormat`.
 *
 * CASES
 *   - a format the table knows        -> its category renders the value
 *   - `bullet` or `none`              -> the level text literal itself
 *   - `custom` or an unknown format   -> decimal, so the label is never silently empty
 *
 * EXAMPLE
 *   formatListNumber(3, "lowerLetter", "")  -> "c"
 *   formatListNumber(4, "upperRoman", "")   -> "IV"
 *   formatListNumber(1, "bullet", "•")      -> "•"
 */
export function formatListNumber(value: number, numberFormat: string, levelTextLiteral: string): string {
    const renderer = RENDERERS_BY_NUMBER_FORMAT.get(numberFormat) ?? RENDERERS_BY_NUMBER_FORMAT.get("decimal")!;
    return renderer(value, levelTextLiteral);
}

/** True for the two formats whose label is the level text itself rather than a rendered counter. */
export function isLiteralNumberFormat(numberFormat: string): boolean {
    return numberFormat === "bullet" || numberFormat === "none";
}
